import { ToimittajaTunnisteService } from '@lemon/core-firebase/service/lasku/toimittaja-tunniste.service'
import { CodeCheckService } from '@lemon/core/service/code-check.service'
import {
  LlmTositeLukuTestitapausLahde,
  LlmTositeLukuToimittajaRekisteritieto,
  TositteenBusinessIdTyyppi,
  TositteenLuetutTiedot,
  TositteenMetatiedot,
  TositteenTiedotKentta,
} from '../model/llm-tosite-luku-testaus'

export interface TositteenOdotetutTiedot {
  odotetut: TositteenLuetutTiedot
  arvioitavat: Set<TositteenTiedotKentta>
}

export type OdotetunToimittajanPeruste = 'valittu' | 'valittu-ei-rekisterissa' | 'tunnus' | 'nimi' | 'ei-osumaa'

export interface OdotettuToimittaja {
  avain: string | null
  peruste: OdotetunToimittajanPeruste
}

const _YHTIOMUOTO_LIITTEET = new Set(['oy', 'oyj', 'ab', 'abp', 'ky', 'tmi', 'ltd', 'gmbh', 'inc', 'llc', 'as', 'plc', 'ry', 'rf', 'oü', 'sa', 'srl', 'bv', 'ag'])

/**
 * Metatiedoista ja rekisterisnapshotista johdetut odotusarvot sekä niiden normalisointi. Jaettu funktioiden ja
 * käyttöliittymän kesken, jotta lomake näyttää saman johdetun tunnustyypin ja toimittajan kuin pisteytys käyttää.
 */
export class LlmTositeLukuMetatiedotService {
  constructor(
    private _toimittajaTunnisteService: ToimittajaTunnisteService,
    private _codeCheckService: CodeCheckService,
  ) {}

  normalisoiNimi(nimi: string | null | undefined): string | null {
    if (!nimi) {
      return null
    }
    const sanat = nimi
      .normalize('NFKC')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim()
      .split(' ')
      .filter(sana => !!sana)
    while (sanat.length > 1 && _YHTIOMUOTO_LIITTEET.has(sanat[0])) {
      sanat.shift()
    }
    while (sanat.length > 1 && _YHTIOMUOTO_LIITTEET.has(sanat[sanat.length - 1])) {
      sanat.pop()
    }
    return sanat.join('') || null
  }

  /** Pienet kirjaimet, välimerkit välilyönneiksi; ei yhtiömuotoliitteiden poistoa, koska tämä vertaa luettua tekstiä sellaisenaan eikä rekisterin nimeen. Tyhjä → null. */
  normalisoiTeksti(teksti: string | null | undefined): string | null {
    if (!teksti) {
      return null
    }
    const normalisoitu = teksti
      .normalize('NFKC')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim()
    return normalisoitu || null
  }

  /** Isot kirjaimet, vain kirjaimet ja numerot. Suomalainen VAT palautetaan y-tunnusmuodossa ilman viivaa, jotta y-tunnus ja VAT vertautuvat samaksi. Tyhjä → null. */
  normalisoiTunnus(tunnus: string | null | undefined): string | null {
    if (!tunnus) {
      return null
    }
    const normalisoitu = tunnus.toUpperCase().replace(/[^A-Z0-9]/g, '')
    if (!normalisoitu) {
      return null
    }
    return this._codeCheckService.annaYTunnus(normalisoitu).replace(/-/g, '')
  }

  /** Luokitellaan samalla säännöllä kuin toimittajarekisterin tunnisteet; tuntematon muoto tai virheellinen tarkiste → null. */
  paatteleBusinessIdType(businessId: string | null | undefined): TositteenBusinessIdTyyppi | null {
    const tunniste = this._toimittajaTunnisteService.luokitteleTunniste((businessId ?? '').replace(/\s+/g, ''))
    return tunniste && tunniste.tyyppi !== 'muu' ? tunniste.tyyppi : null
  }

  paatteleOdotettuToimittajaAvain(metatiedot: TositteenMetatiedot, toimittajaRekisteri: LlmTositeLukuToimittajaRekisteritieto[]): string | null {
    return this.paatteleOdotettuToimittaja(metatiedot, toimittajaRekisteri).avain
  }

  /**
   * Testerin valinta ohittaa päättelyn. Ilman valintaa rekisterin toimittaja, jonka tunnisteisiin syötetty tunnus
   * normalisoituna kuuluu; muuten jonka normalisoitu nimi on sama; muuten null.
   */
  paatteleOdotettuToimittaja(metatiedot: TositteenMetatiedot, toimittajaRekisteri: LlmTositeLukuToimittajaRekisteritieto[]): OdotettuToimittaja {
    if (metatiedot.toimittajaAvain === null) {
      return { avain: null, peruste: 'valittu' }
    }
    if (metatiedot.toimittajaAvain) {
      const loytyy = toimittajaRekisteri.some(t => t.avain === metatiedot.toimittajaAvain)
      return { avain: metatiedot.toimittajaAvain, peruste: loytyy ? 'valittu' : 'valittu-ei-rekisterissa' }
    }
    const tunnus = this.normalisoiTunnus(metatiedot.businessId)
    if (tunnus) {
      const tunnuksella = toimittajaRekisteri.find(t => (t.tunnisteet || []).some(tunniste => this.normalisoiTunnus(tunniste) === tunnus))
      if (tunnuksella) {
        return { avain: tunnuksella.avain, peruste: 'tunnus' }
      }
    }
    const nimi = this.normalisoiNimi(metatiedot.toimittaja)
    if (nimi) {
      const nimella = toimittajaRekisteri.find(t => this.normalisoiNimi(t.nimi) === nimi)
      if (nimella) {
        return { avain: nimella.avain, peruste: 'nimi' }
      }
    }
    return { avain: null, peruste: 'ei-osumaa' }
  }

  /**
   * Odotusarvot ja arvioitavat kentät. toimittaja, businessId ja businessIdType arvioidaan aina (businessIdType vain jos tunnuksen
   * muoto tunnistetaan tai tunnus on null). paivamaara, summa ja tyyppi arvioidaan vain jos syötetty. toimittajaAvain arvioidaan vain
   * lemonaid-tapauksille, joilla on rekisterisnapshot.
   */
  muodostaOdotetutTiedot(lahde: LlmTositeLukuTestitapausLahde, metatiedot: TositteenMetatiedot, toimittajaRekisteri: LlmTositeLukuToimittajaRekisteritieto[]): TositteenOdotetutTiedot {
    const toimittaja = metatiedot.toimittaja?.trim() || null
    const businessId = metatiedot.businessId?.trim() || null
    const paivamaara = metatiedot.paivamaara?.trim() || null
    const summa = typeof metatiedot.summa === 'number' && Number.isFinite(metatiedot.summa) ? metatiedot.summa : null
    const businessIdType = this.paatteleBusinessIdType(businessId)
    const tyyppi = metatiedot.tyyppi === 'ostolasku' || metatiedot.tyyppi === 'kuitti' ? metatiedot.tyyppi : null
    const arvioiToimittajaAvain = lahde === 'lemonaid' && toimittajaRekisteri.length > 0

    const arvioitavat = new Set<TositteenTiedotKentta>(['toimittaja', 'businessId'])
    if (businessId === null || businessIdType !== null) {
      arvioitavat.add('businessIdType')
    }
    if (paivamaara !== null) {
      arvioitavat.add('paivamaara')
    }
    if (summa !== null) {
      arvioitavat.add('summa')
    }
    if (arvioiToimittajaAvain) {
      arvioitavat.add('toimittajaAvain')
    }
    if (tyyppi !== null) {
      arvioitavat.add('tyyppi')
    }

    return {
      odotetut: {
        paivamaara,
        summa,
        toimittaja,
        businessId,
        businessIdType,
        toimittajaAvain: arvioiToimittajaAvain ? this.paatteleOdotettuToimittajaAvain({ toimittaja, businessId, toimittajaAvain: metatiedot.toimittajaAvain }, toimittajaRekisteri) : null,
        tyyppi,
      },
      arvioitavat,
    }
  }

  onkoTaydellinenOsuma(osumat: (boolean | null)[]): boolean {
    const arvioidut = osumat.filter(osuma => osuma !== null)
    return arvioidut.length > 0 && arvioidut.every(osuma => osuma === true)
  }
}
